import React, { useEffect, useState } from 'react';
import type { GetInitialPropsContext } from 'Src/types/common/common';
import type { CMNextPageComponent } from 'Src/types/server-helpers';
import type { InfoBannerData } from 'Components/widgets/info-banner/infoBanner.query';
import URI from 'urijs';
import * as routeBuilder from 'Routes/routeBuilder';
import { STATUS_CODE_404 } from 'Utils/status-code/statusCode.constants';
import * as pleUtils from 'Utils/ple-utils';
import { HeaderLite } from 'Components/widgets/header-lite/HeaderLite';
import { BookingBreadcrumb } from 'Components/widgets/booking-breadcrumb/BookingBreadcrumb';
import { BookingLayout } from 'Views/booking/BookingLayout';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { proposalsByIdQuery } from 'Views/booking/BookingEngine.query';
import { fetchBookingEngineI18nQuery } from 'Views/booking/BookingEngineI18n.query';
import { addTranslationKeys, t } from 'Src/i18n';
import { useRouter } from 'next/router';
import {
  NAPAY_PSP_ID,
  NODELCRERE_CHEQUE_PSP_ID,
  NODELCRERE_TRANSFER_PSP_ID,
  PAYMENT_METHOD_CATEGORY,
} from 'Components/widgets/payment-info/PaymentInfo.const';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { useGQLErrorsHandler } from 'Utils/use-gql-errors-handler/useGQLErrorsHandler';
import appConfig from 'Config';
import { useAppContext } from 'Context/ReactAppContext';
import { useDispatch, useSelector } from 'react-redux';
import { SET_PROPOSALS } from 'Store/booking/proposals/constants';
import { getCurrentProposal } from 'Store/booking/booking.selector';
import { selectProposal } from 'Store/booking/selected-proposal-id/selectedProposalId.action';
import graphqlClient from 'Utils/graphql-client/graphqlClient';
import { PAYMENT_BOOKING_STATUS } from 'Utils/request/requestDictionary';
import { B2BBankingView } from 'Components/widgets/payment-info/b2b-payment-info/B2BBankingView';
import { TrackPage } from 'Tracking/track-page/TrackPage';
import { STATIC_PAGE_TYPES, TRACK_PAGE_NAMES } from 'Tracking/constants';
import { DelCredereBooking } from 'Components/widgets/payment-info/b2b-payment-info/del-credere-booking/DelCredereBooking';
import { isEmpty } from 'lodash';
import { queryParamToString } from 'Src/types/server-helpers';

type ProposalPrePaymentUrlParams = {
  proposalId: string;
};

type ProposalPrePaymentQueryParams = {
  bookingId?: string;
  payment_error?: string;
};

type RedirectResponse = {
  redirect: (url: string) => void;
};

type B2BBookingEngineProps = {
  attendeesPaymentStatus: AttendeePaymentStatus[];
  proposal: Proposal;
  resort: ProductWithAccommodations;
  i18n: I18N;
  sellerProviders: PaymentProvider[];
  error?: unknown;
};

const B2BBookingEngine = ({
  attendeesPaymentStatus,
  proposal,
  resort,
  i18n,
  sellerProviders,
  error,
}: B2BBookingEngineProps) => {
  const router = useRouter();
  const { locale, cmta } = useAppContext();
  const dispatch = useDispatch();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    dispatch({ type: SET_PROPOSALS, payload: { proposals: [proposal] } });
    dispatch(selectProposal(proposal.id));
    setIsClient(true);
  }, [dispatch, proposal]);

  const currentProposal = useSelector(getCurrentProposal);

  useGQLErrorsHandler(error, { catchAuthError: true });

  if (error || isEmpty(currentProposal)) {
    return null;
  }

  const b2bBeUrl = `${appConfig.get('b2bBookingEngine.url')}/${locale}/${currentProposal.id}/summary`;

  const goToNextStep = (validationCallback?: () => Promise<void>) => async () => {
    await validationCallback?.();
  };

  return (
    <div>
      <TrackPage
        pageName={TRACK_PAGE_NAMES.BE_BANKING}
        payload={{
          proposal_id: currentProposal.id,
          resort_name: resort.name,
          resort_code: resort.id,
        }}
      />
      <HeaderLite actions={[]} proposal={currentProposal} product={resort} />
      <BookingBreadcrumb
        items={[]}
        selectedProposal={currentProposal}
        resort={resort}
        backgroundImageSrc={resort.image}
        canEdit={false}
        activeStep={0}
        onClickStep={() => undefined}
        i18n={i18n}
        actionGA="banking"
      />
      <div className="min-h-[60vh] py-20">
        <SiteContainer size="small">
          <div className="mb-20">
            <BookingLayout
              isOnLastPage
              currentPage="banking"
              price={currentProposal.price}
              previousAction={() => router.push(b2bBeUrl)}
              goToNextStep={goToNextStep}
              selectedProposal={currentProposal}
              resort={resort}
            >
              {isClient &&
                (cmta.isDelCredere ? (
                  <DelCredereBooking
                    i18n={i18n}
                    selectedProposal={currentProposal}
                    totalPrice={currentProposal.price?.value ?? 0}
                    renderBottomNavbar={() => null}
                  />
                ) : (
                  <B2BBankingView
                    attendeesPaymentStatus={attendeesPaymentStatus}
                    additionalProviders={sellerProviders}
                    i18n={i18n}
                    selectedProposal={currentProposal}
                    resort={resort}
                    infoBannerData={{} as InfoBannerData}
                    paymentProviders={[]}
                  />
                ))}
            </BookingLayout>
          </div>
        </SiteContainer>
        <div className="h-[128px]" />
      </div>
    </div>
  );
};

const B2BPrePaymentPage = B2BBookingEngine as CMNextPageComponent;

B2BPrePaymentPage.getCMInitialProps = async (ctx) => {
  const {
    appContext,
    res,
    location: { origin, urlParams, queryParams },
  } = ctx as unknown as GetInitialPropsContext<
    ProposalPrePaymentUrlParams,
    ProposalPrePaymentQueryParams
  >;
  const { isSeller } = appContext;

  if (!isSeller) {
    return {
      statusCode: STATUS_CODE_404,
    };
  }
  const { proposalId } = urlParams;
  const bookingId = queryParamToString(queryParams.bookingId);
  const redirectRes = res as RedirectResponse | null | undefined;

  try {
    const { proposals } = await proposalsByIdQuery(
      { proposalsId: [proposalId], shouldRequestPayment: true },
      appContext,
    );
    const { config, legalInformations } = await fetchBookingEngineI18nQuery(appContext);
    const i18n = {
      ...config,
      ...legalInformations,
    };

    let attendeesPaymentStatus: AttendeePaymentStatus[] = [];

    if (bookingId) {
      const attendeesPaymentStatusResponse = await graphqlClient.runNoCacheQuery(
        `query {
      sellerBooking (bookingId:"${bookingId}") {
        bookingStatus
        attendees {
          customerId
          hasPaid
        }
      }
    }
    `,
        appContext,
        {
          requestName: PAYMENT_BOOKING_STATUS,
        },
      );

      attendeesPaymentStatus = attendeesPaymentStatusResponse?.sellerBooking?.attendees || [];
    }

    addTranslationKeys(i18n);

    const sellerProviders: PaymentProvider[] = [];

    if (
      appContext.isCmta &&
      !appContext.cmta.isDelCredere &&
      isFeatureFlipped(appContext)('cmta.booking.takeOption')
    ) {
      if (isFeatureFlipped(appContext)('booking.cmta.bankTransfer')) {
        sellerProviders.push({
          description: t('booking.banking.paymentProvider.bankTransfer.title'),
          categoryPaymentMethod: PAYMENT_METHOD_CATEGORY.BANK_TRANSFER,
          id: NODELCRERE_TRANSFER_PSP_ID,
          selectorId: NODELCRERE_TRANSFER_PSP_ID,
          paymentMethods: [],
          ignore: true,
        } as unknown as PaymentProvider);
      }
      if (isFeatureFlipped(appContext)('booking.cmta.cheque')) {
        sellerProviders.push({
          description: t('booking.banking.paymentProvider.cheque.title'),
          categoryPaymentMethod: PAYMENT_METHOD_CATEGORY.CHEQUE,
          id: NODELCRERE_CHEQUE_PSP_ID,
          selectorId: NODELCRERE_CHEQUE_PSP_ID,
          paymentMethods: [],
          ignore: true,
        } as unknown as PaymentProvider);
      }
    }

    if (appContext.isCxo) {
      sellerProviders.push({
        id: NAPAY_PSP_ID,
        categoryPaymentMethod: '',
        selectorId: NAPAY_PSP_ID,
        paymentMethods: [],
        description: null,
        logo: null,
        label: NAPAY_PSP_ID,
        requiredDelayBeforeDeparture: 0,
      } as unknown as PaymentProvider);
    }

    return {
      attendeesPaymentStatus,
      proposal: proposals.proposals[0],
      resort: proposals.resort,
      i18n,
      sellerProviders,
    } as Record<string, unknown>;
  } catch (err) {
    if (!pleUtils.hasAuthResponseError(err) && redirectRes) {
      redirectRes.redirect(
        new URI(routeBuilder.buildUrl('proposal', { proposalId }))
          .origin(origin)
          .query({ ...queryParams, payment_error: true })
          .toString(),
      );
    }
    return { error: err } as Record<string, unknown>;
  }
};

B2BPrePaymentPage.disableFooter = true;
B2BPrePaymentPage.disableHeader = true;
B2BPrePaymentPage.requireAuthentication = true;
B2BPrePaymentPage.pageType = STATIC_PAGE_TYPES.BE;

export default B2BPrePaymentPage;
